// useTenantEntitlements
// ----------------------
// Shared hook exposing the tenant's per-slot ("app license") view to every
// surface that reasons about per-app pentest budget: the Applications rows, the
// Pentest-Now gate on the WebApplication page, the Add-App license picker, and
// the Billing license list.
//
// One source of truth so the "N pentests left" cue on a row and the Pentest
// button's blocked-reason prompt can't disagree. Quota is per web app, so this
// replaces the tenant-wide getTenantUsage read for pentest surfaces (that query
// now only backs seats + the tier label).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deriveEntitlementView,
  EntitlementView,
  fetchTenantEntitlements,
  TenantEntitlement,
} from "@/services/tenantEntitlements.service";
import { CacheKey, EPOCH_TO_JS_DATE_CONVERSION } from "@/constants/definitions";
import { cacheThenRefresh, setCache } from "@/utils/cache";
import { ENTITLEMENTS_UPDATE, eventBus } from "@/services/eventBus.service";

interface UseTenantEntitlementsResult {
  entitlements: TenantEntitlement[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  // The license bound to a given web app as a derived view, or null when this
  // read hasn't landed (or failed). Every app has a license server-side, so null
  // is an unknown, not an exemption — callers stay permissive on it only because
  // the backend gate is authoritative and fails closed.
  entitlementForWebApp: (webAppId: string) => EntitlementView | null;
  // Count of licenses bought but not yet attached to an app.
  unboundCount: number;
}

export function useTenantEntitlements(): UseTenantEntitlementsResult {
  const [entitlements, setEntitlements] = useState<TenantEntitlement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);

  // Initial read is cache-then-refresh — the same strategy the login bootstrap
  // uses for its own preloads, and warmTenantEntitlementsCache() has usually
  // populated that cache by the time any surface mounts. That's what stops the
  // Add-App license picker from blocking on a round trip before it can paint.
  //
  // Every subsequent pass (an explicit refresh(), or a purchase broadcast) goes
  // straight to the network: re-applying the cache first would flash the very
  // state the caller is trying to move on from — the license list as it was
  // BEFORE the purchase.
  useEffect(() => {
    let cancelled = false;
    // Tracks whether anything has been rendered yet this pass, so a failed
    // fetch that still has cached rows behind it doesn't replace real data
    // with an error banner.
    let hasAppliedRows = false;
    setIsLoading(true);
    setError(null);

    const applyRows = (rows: TenantEntitlement[]) => {
      if (cancelled) return;
      hasAppliedRows = true;
      setEntitlements(rows);
      setIsLoading(false);
    };

    const applyError = (fetchError: unknown) => {
      if (cancelled) return;
      setIsLoading(false);
      // Cached rows are already on screen — surfacing an error over the top of
      // them would be misleading. The next refresh gets another go.
      if (hasAppliedRows) return;
      setError(
        fetchError instanceof Error
          ? fetchError.message
          : "Failed to load app licenses",
      );
    };

    const isInitialLoad = refreshCounter === 0;
    if (isInitialLoad) {
      cacheThenRefresh<TenantEntitlement[]>(
        CacheKey.listTenantEntitlements,
        fetchTenantEntitlements,
        applyRows,
        { onError: applyError },
      );
    } else {
      fetchTenantEntitlements()
        .then((rows) => {
          applyRows(rows);
          // Write through so the next surface to mount — Add App, typically —
          // reads the post-purchase list from cache rather than the stale one.
          setCache(CacheKey.listTenantEntitlements, rows);
        })
        .catch(applyError);
    }

    return () => {
      cancelled = true;
    };
  }, [refreshCounter]);

  const refresh = useCallback(
    () => setRefreshCounter((current) => current + 1),
    [],
  );

  // A purchase anywhere in the app invalidates every mounted instance. Without
  // this, buying a license on the Billing page would leave the Add-App picker
  // and the Pentest gate holding a pre-purchase list until their next mount.
  useEffect(() => {
    eventBus.on(ENTITLEMENTS_UPDATE, refresh);
    return () => {
      eventBus.off(ENTITLEMENTS_UPDATE, refresh);
    };
  }, [refresh]);

  // Index by boundWebAppId once per fetch so per-row lookups are O(1).
  const byWebApp = useMemo(() => {
    const map = new Map<string, TenantEntitlement>();
    for (const entitlement of entitlements) {
      if (entitlement.boundWebAppId) {
        map.set(entitlement.boundWebAppId, entitlement);
      }
    }
    return map;
  }, [entitlements]);

  const entitlementForWebApp = useCallback(
    (webAppId: string): EntitlementView | null => {
      const entitlement = byWebApp.get(webAppId);
      if (!entitlement) return null;
      // Seconds — matches the BE epoch fields (accessExpiresAt).
      const nowEpochSeconds = Math.floor(
        Date.now() / EPOCH_TO_JS_DATE_CONVERSION,
      );
      return deriveEntitlementView(entitlement, nowEpochSeconds);
    },
    [byWebApp],
  );

  const unboundCount = useMemo(
    () => entitlements.filter((e) => e.status === "unbound").length,
    [entitlements],
  );

  return {
    entitlements,
    isLoading,
    error,
    refresh,
    entitlementForWebApp,
    unboundCount,
  };
}
